/*
 * PlanCard: a bPanel plan as on the landing's pricing cards: name, price with the riyal sign, cycle,
 * hosting accounts, AI credits and extras. Two uses:
 *   const { plans } = usePlanCatalog()
 *   <PlanCard plan={plan} cycle="yearly" />                                 display (featured plan in navy)
 *   <PlanPicker plans={plans} value={planId} onValueChange={setPlanId} cycle={cycle} current="team" />
 * Plans and prices come from the API (GET /plans: today's prices, D25); the picker shows a skeleton while
 * they load. `currentPerMonth` shows the client's own price on the current plan when it differs (an older
 * price version). Never show savings claims here.
 */
import { Check, Plus, Sparkle, UsersThree } from '@phosphor-icons/react'
import { RadioGroup } from 'radix-ui'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { Sar } from '@/lib/format'
import { Bidi } from '@/lib/bidi'
import type { ApiPlan } from '@/api/types'
import type { Cycle, PlanId } from '@/lib/plans'
import { useFormat } from '@/i18n/useFormat'
import { Skeleton } from './Skeleton'

type Plan = ApiPlan

function PlanFeatures({ plan, inverted, inline }: { plan: Plan; inverted?: boolean; inline?: boolean }) {
  const L = inline ? 'span' : 'ul'
  const I = inline ? 'span' : 'li'
  const { t } = useTranslation('common')
  const f = useFormat()
  const icon = cn('size-[18px] shrink-0', inverted ? 'text-white' : 'text-brand-600')
  const extras: ReactNode[] = []
  if (plan.features.includes('reseller')) extras.push(t('plans.extras.reseller'))
  if (plan.features.includes('white_label')) extras.push(t('plans.extras.whiteLabel'))
  if (plan.extraAccount)
    extras.push(
      <span className="inline-flex flex-wrap items-baseline gap-1">
        {t('plans.extras.extraAccount')} <Sar amount={plan.extraAccount} className="font-semibold" />
      </span>,
    )
  return (
    <L className={cn('flex flex-col gap-2.5 text-13', inverted ? 'text-white/85' : 'text-body')}>
      <I className={cn('flex items-center gap-2.5 font-semibold', inverted ? 'text-white' : 'text-ink')}>
        <UsersThree className={icon} weight="duotone" aria-hidden="true" />
        <Bidi text={t('plans.accounts', { count: plan.accounts, n: f.number(plan.accounts) })} />
      </I>
      <I className={cn('flex items-center gap-2.5 font-semibold', inverted ? 'text-white' : 'text-ink')}>
        <Sparkle className={icon} weight="duotone" aria-hidden="true" />
        <Bidi text={t('plans.aiCredits', { n: f.number(plan.aiCredits) })} />
      </I>
      <I className="flex items-center gap-2.5">
        <Check className={cn(icon, 'size-4')} weight="bold" aria-hidden="true" />
        <Bidi text={t('plans.allFeatures')} />
      </I>
      {extras.map((e, i) => (
        <I key={i} className="flex items-center gap-2.5">
          <Plus className={cn(icon, 'size-4')} weight="bold" aria-hidden="true" />
          {typeof e === 'string' ? <Bidi text={e} /> : e}
        </I>
      ))}
    </L>
  )
}

function PriceLine({ plan, cycle, inverted }: { plan: Plan; cycle: Cycle; inverted?: boolean }) {
  const { t } = useTranslation('common')
  return (
    <div>
      <Sar
        amount={plan[cycle]}
        className={cn(
          'font-display text-40 leading-none font-semibold tracking-[-0.02em]',
          inverted ? 'text-white' : 'text-ink-deep',
        )}
        signClassName="text-[0.6em]"
      />
      <p className={cn('mt-2 text-12', inverted ? 'text-white/70' : 'text-subtle')}>
        {cycle === 'monthly' ? t('plans.perMonth') : t('plans.perMonthYearly')}
      </p>
    </div>
  )
}

export function PlanCard({
  plan,
  cycle,
  action,
  current,
  className,
}: {
  plan: Plan
  cycle: Cycle
  /** a button under the price */
  action?: ReactNode
  /** marks the client's current plan */
  current?: boolean
  className?: string
}) {
  const { t } = useTranslation('common')
  const featured = Boolean(plan.featured)
  return (
    <article
      aria-labelledby={`plan-${plan.id}`}
      className={cn(
        'relative flex flex-col overflow-hidden rounded-panel p-6',
        featured
          ? 'bg-[linear-gradient(160deg,var(--color-brand-500)_0%,var(--color-brand-800)_48%,var(--color-brand-950)_100%)] text-white shadow-float'
          : 'bg-surface text-ink-deep shadow-panel ring-1 ring-card-line',
        className,
      )}
    >
      {featured ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 [background-image:radial-gradient(circle_at_1px_1px,var(--color-white)_1px,transparent_0)] [mask-image:linear-gradient(to_bottom,black,transparent_70%)] [background-size:18px_18px] opacity-[0.16]"
        />
      ) : null}
      <div className="relative flex min-h-7 items-center justify-between gap-3">
        <h3
          id={`plan-${plan.id}`}
          className={cn('font-display text-22 font-semibold', featured ? 'text-white' : 'text-ink-deep')}
        >
          {plan.name}
        </h3>
        {current ? (
          <span
            className={cn(
              'rounded-full px-2.5 py-1 text-12 font-semibold',
              featured ? 'bg-white/15 text-white ring-1 ring-white/25' : 'bg-teal-50 text-teal-700',
            )}
          >
            {t('plans.current')}
          </span>
        ) : featured ? (
          <span className="rounded-full bg-white/15 px-2.5 py-1 text-12 font-semibold text-white ring-1 ring-white/25">
            {t('plans.featured')}
          </span>
        ) : null}
      </div>
      <p
        className={cn(
          'relative mt-1.5 min-h-10 text-13 leading-5',
          featured ? 'text-white/75' : 'text-muted',
        )}
      >
        {t(`plans.taglines.${plan.id}`)}
      </p>
      <div className="relative mt-5">
        <PriceLine plan={plan} cycle={cycle} inverted={featured} />
      </div>
      {action ? <div className="relative mt-5">{action}</div> : null}
      <div className={cn('relative mt-6 border-t pt-5', featured ? 'border-white/15' : 'border-hairline')}>
        <PlanFeatures plan={plan} inverted={featured} />
      </div>
    </article>
  )
}

export function PlanPicker({
  value,
  onValueChange,
  cycle,
  current,
  currentPerMonth,
  plans,
  columns = 4,
  className,
  'aria-label': ariaLabel,
}: {
  value?: PlanId
  onValueChange: (id: PlanId) => void
  cycle: Cycle
  current?: PlanId
  /** the client's own price per month on the current plan and cycle (shown when it differs from today's) */
  currentPerMonth?: number
  /** from GET /plans; undefined while loading */
  plans: readonly Plan[] | undefined
  /** columns at the widest: 4 (one row on wide screens) or 2 for narrow containers; one column on phones */
  columns?: 2 | 4
  className?: string
  'aria-label'?: string
}) {
  const { t } = useTranslation('common')
  if (!plans)
    return (
      <div className={cn('grid gap-3 sm:grid-cols-2', columns === 4 && 'xl:grid-cols-4', className)}>
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-64 rounded-panel" />
        ))}
      </div>
    )
  return (
    <RadioGroup.Root
      value={value}
      onValueChange={(v) => onValueChange(v as PlanId)}
      aria-label={ariaLabel ?? t('plans.choose')}
      className={cn('grid gap-3 sm:grid-cols-2', columns === 4 && 'xl:grid-cols-4', className)}
    >
      {plans.map((plan) => (
        <RadioGroup.Item
          key={plan.id}
          value={plan.id}
          className={cn(
            'group relative flex flex-col rounded-panel bg-surface p-5 text-start shadow-panel ring-1 ring-card-line transition-[box-shadow,background-color] duration-150 ring-inset',
            'hover:ring-card-line-hover data-[state=checked]:bg-brand-50/40 data-[state=checked]:ring-2 data-[state=checked]:ring-brand-600',
          )}
        >
          <span className="flex items-center justify-between gap-2">
            <span className="font-display text-18 font-semibold text-ink-deep">{plan.name}</span>
            <span className="flex items-center gap-2">
              {current === plan.id ? (
                <span className="rounded-full bg-teal-50 px-2 py-0.5 text-12 font-semibold text-teal-700">
                  {t('plans.current')}
                </span>
              ) : plan.featured ? (
                <span className="rounded-full bg-brand-50 px-2 py-0.5 text-12 font-semibold text-brand-700">
                  {t('plans.featuredShort')}
                </span>
              ) : null}
              <span className="grid size-5 place-items-center rounded-full border border-hairline-strong bg-surface transition-colors group-data-[state=checked]:border-brand-600 group-data-[state=checked]:bg-brand-600">
                <Check
                  className="size-3 text-white opacity-0 group-data-[state=checked]:opacity-100"
                  weight="bold"
                  aria-hidden="true"
                />
              </span>
            </span>
          </span>
          <span className="mt-1 block min-h-10 text-13 leading-5 text-muted">
            {t(`plans.taglines.${plan.id}`)}
          </span>
          <span className="mt-4 block">
            <Sar
              amount={plan[cycle]}
              className="font-display text-28 leading-none font-semibold text-ink-deep"
              signClassName="text-[0.6em]"
            />
            <span className="mt-1.5 block text-12 text-subtle">
              {cycle === 'monthly' ? t('plans.perMonth') : t('plans.perMonthYearly')}
            </span>
            {current === plan.id && currentPerMonth != null && currentPerMonth !== plan[cycle] ? (
              <span className="mt-2 inline-flex flex-wrap items-baseline gap-1 rounded-full bg-teal-50 px-2 py-0.5 text-12 font-semibold text-teal-700">
                {t('plans.yourPrice')} <Sar amount={currentPerMonth} />
              </span>
            ) : null}
          </span>
          <span className="mt-4 block border-t border-hairline pt-4">
            <PlanFeatures plan={plan} inline />
          </span>
        </RadioGroup.Item>
      ))}
    </RadioGroup.Root>
  )
}
